import React from 'react'

import { ConcurrentTree } from 'components/scenarios/concurrent/ConcurrentTree'
import { NodeListIcon } from 'components/scenarios/concurrent/ConcurrentTreeIcons'
import { ConcurrentTreeNodeSectionDescription } from 'components/scenarios/concurrent/ConcurrentTreeNodeSectionDescription'
import { ConcurrentTreeNodesRoot } from 'components/scenarios/concurrent/ConcurrentTreeNodesRoot'
import { ConcurrentTreeViewNode } from 'components/scenarios/concurrent/ConcurrentTreeViewNode'
import { useGraphEditorContext } from 'components/scenarios/concurrent/GraphEditorContext'
import {
  NODE_SECTION_LABELS,
  NodeSectionType
} from 'components/scenarios/utils/graph/messaging'
import { getOrderedNodesFromBranch } from 'components/scenarios/utils/graph/nodes/getters'
import { isConcurrentNode } from 'components/scenarios/utils/graph/nodes/typeguards'
import {
  activeInfraClientSelector,
  activeK8sClientSelector,
  useInfraClientsStore,
  useK8sClientsStore
} from 'hooks/use-clients'

import type { ScenarioPreview } from 'types/api/reliability-tests/preview'
import type { GraphNode } from 'types/api/scenarios/scenario'

function getAllBranchNodeGuids(node: GraphNode): string[] {
  if (isConcurrentNode(node)) {
    const guids = [node.guid]
    node.branches.forEach((b, i) => {
      guids.push(`${node.guid}-branch-${i}`)
      Object.values(b.nodes).forEach((n) => {
        const branchNodeIds = getAllBranchNodeGuids(n)
        guids.push(...branchNodeIds)
      })
    })
    return guids
  }
  return []
}

export const ConcurrentTreeView: React.FC<{
  scenarioPreview?: ScenarioPreview
  onClick?: (node: GraphNode) => void
  isClickable?: boolean
  hasRun?: boolean
}> = ({ scenarioPreview, onClick, isClickable, hasRun }) => {
  const { graph } = useGraphEditorContext()

  /*
   * start_id should always be `concurrentNode` which should be the parent of the attack nodes
   * or start_id is zero if there's no concurrent nodes
   */
  const startNode = graph.nodes[graph.start_id]
  const branchIds = Object.values(graph.nodes ?? {})
    .map(getAllBranchNodeGuids)
    .flat()

  const activeClients = useInfraClientsStore(activeInfraClientSelector)
  const k8sClients = useK8sClientsStore(activeK8sClientSelector)

  const nodeSectionLabel = NODE_SECTION_LABELS[NodeSectionType.Nodes]

  if (isConcurrentNode(startNode)) {
    return (
      <ConcurrentTree expanded={['root', ...branchIds]}>
        <ConcurrentTreeViewNode
          activeClients={activeClients}
          hasRun={hasRun}
          isClickable={isClickable}
          k8sClients={k8sClients}
          node={startNode}
          scenarioPreview={scenarioPreview}
          onClick={onClick}
        />
      </ConcurrentTree>
    )
  }

  return (
    <ConcurrentTree expanded={['root', ...branchIds]}>
      <ConcurrentTreeNodesRoot
        icon={<NodeListIcon />}
        label={nodeSectionLabel}
        nodeId="root"
      >
        <ConcurrentTreeNodeSectionDescription
          sectionType={NodeSectionType.Nodes}
        />
        {getOrderedNodesFromBranch(graph).map(([, node]) => {
          return (
            <ConcurrentTreeViewNode
              key={node.guid}
              activeClients={activeClients}
              hasRun={hasRun}
              isClickable={isClickable}
              k8sClients={k8sClients}
              node={node}
              scenarioPreview={scenarioPreview}
              onClick={onClick}
            />
          )
        })}
      </ConcurrentTreeNodesRoot>
    </ConcurrentTree>
  )
}
